import React, { useEffect, useState, useCallback } from 'react';
import { KeyRound, Copy, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Button } from '@/components/ui/Button/Button';
import { Input } from '@/components/ui/Input/Input';
import { Badge } from '@/components/ui/Badge/Badge';
import { Modal } from '@/components/ui/Modal/Modal';
import { Alert } from '@/components/ui/Alert/Alert';
import { Spinner } from '@/components/ui/Spinner';
import { useNotificationStore } from '@/stores/notificationStore';
import { useOrgStore } from '@/stores/orgStore';
import {
  apiKeyService,
  type ApiKeySafe,
  type CreateApiKeyResponse,
} from '@/services/apiKeyService';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Create Key Modal
// ---------------------------------------------------------------------------

function CreateKeyModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (result: CreateApiKeyResponse) => void;
}) {
  const orgId = useOrgStore((s) => s.activeOrgId);
  const notify = useNotificationStore();
  const [name, setName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!orgId || !name.trim()) return;
    setIsCreating(true);
    try {
      const result = await apiKeyService.create(orgId, { name: name.trim() });
      onCreated(result);
      setName('');
    } catch (err: any) {
      notify.error(err.message || 'Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Create API Key"
      description="Give your key a descriptive name to identify its purpose."
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleCreate}
            disabled={!name.trim() || isCreating}
            loading={isCreating}
          >
            Create Key
          </Button>
        </div>
      }
    >
      <div className="py-2">
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Key Name
        </label>
        <Input
          placeholder="e.g. Production API, Zapier Integration"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          autoFocus
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// New Key Display (shown once after creation)
// ---------------------------------------------------------------------------

function NewKeyAlert({
  rawKey,
  onDismiss,
}: {
  rawKey: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(rawKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = rawKey;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <Alert variant="info" className="mb-6">
      <div>
        <p className="font-medium text-sm mb-1">Your new API key</p>
        <p className="text-xs text-gray-600 mb-3">
          Copy this key now — it won't be shown again.
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-gray-100 text-gray-800 px-3 py-2 rounded-md text-sm font-mono break-all select-all">
            {rawKey}
          </code>
          <Button variant="outline" size="sm" onClick={handleCopy}>
            {copied ? (
              <span className="text-green-600 text-xs">Copied!</span>
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
        </div>
        <button
          onClick={onDismiss}
          className="mt-3 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          I've copied my key
        </button>
      </div>
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// Revoke Confirmation Modal
// ---------------------------------------------------------------------------

function RevokeModal({
  keyName,
  isOpen,
  onClose,
  onConfirm,
  isRevoking,
}: {
  keyName: string;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isRevoking: boolean;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Revoke API Key"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={onConfirm}
            disabled={isRevoking}
            loading={isRevoking}
          >
            Revoke Key
          </Button>
        </div>
      }
    >
      <p className="text-sm text-gray-600">
        Are you sure you want to revoke <strong>{keyName}</strong>? Any integrations using this key will immediately stop working. This action cannot be undone.
      </p>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Key Row
// ---------------------------------------------------------------------------

function ApiKeyRow({
  apiKey,
  onRevoke,
}: {
  apiKey: ApiKeySafe;
  onRevoke: (key: ApiKeySafe) => void;
}) {
  const isRevoked = !!apiKey.revoked_at;
  const isExpired = apiKey.expires_at && new Date(apiKey.expires_at) < new Date();

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatRelative = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return formatDate(dateStr);
  };

  return (
    <div
      className={cn(
        'flex items-start sm:items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-lg border transition-colors',
        isRevoked ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-200 hover:border-gray-300'
      )}
    >
      <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 mt-0.5 sm:mt-0">
        <KeyRound className="w-4 h-4 text-gray-500" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm text-gray-900 truncate">{apiKey.name}</span>
          {isRevoked && <Badge variant="error" className="text-xs">Revoked</Badge>}
          {!isRevoked && isExpired && <Badge variant="warning" className="text-xs">Expired</Badge>}
        </div>
        <code className="block mt-0.5 text-xs text-gray-400 font-mono">{apiKey.key_prefix}...</code>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-xs text-gray-400">
          <span>Created {formatDate(apiKey.created_at)}</span>
          <span>Last used: {formatRelative(apiKey.last_used_at)}</span>
        </div>
      </div>

      {!isRevoked && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRevoke(apiKey)}
          className="text-gray-400 hover:text-error-600 shrink-0"
          title="Revoke key"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function Integrations() {
  const orgId = useOrgStore((s) => s.activeOrgId);
  const notify = useNotificationStore();

  const [keys, setKeys] = useState<ApiKeySafe[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newRawKey, setNewRawKey] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySafe | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  // Fetch keys
  const loadKeys = useCallback(async () => {
    if (!orgId) return;
    try {
      const data = await apiKeyService.list(orgId);
      setKeys(data);
    } catch (err: any) {
      notify.error(err.message || 'Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleCreated = (result: CreateApiKeyResponse) => {
    setShowCreate(false);
    setNewRawKey(result.rawKey);
    setKeys((prev) => [result.apiKey, ...prev]);
    notify.success('API key created');
  };

  const handleRevoke = async () => {
    if (!orgId || !revokeTarget) return;
    setIsRevoking(true);
    try {
      await apiKeyService.revoke(orgId, revokeTarget.id);
      setKeys((prev) =>
        prev.map((k) =>
          k.id === revokeTarget.id ? { ...k, revoked_at: new Date().toISOString() } : k
        )
      );
      notify.success('API key revoked');
      setRevokeTarget(null);
    } catch (err: any) {
      notify.error(err.message || 'Failed to revoke key');
    } finally {
      setIsRevoking(false);
    }
  };

  const activeKeys = keys.filter((k) => !k.revoked_at);
  const revokedKeys = keys.filter((k) => !!k.revoked_at);

  return (
    <PageContainer>
      <PageHeader
        title="Integrations"
        description="Manage API keys for programmatic access to your organization's templates and generation API."
      >
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
          <KeyRound className="w-4 h-4 mr-1.5" />
          Create API Key
        </Button>
      </PageHeader>

      {/* New key alert */}
      {newRawKey && (
        <NewKeyAlert rawKey={newRawKey} onDismiss={() => setNewRawKey(null)} />
      )}

      {/* API Documentation */}
      <div className="mb-6 p-5 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="text-sm font-semibold text-gray-800 mb-1">API Reference</h3>
        <p className="text-xs text-gray-500 mb-4">
          Authenticate all requests with your API key in the <code className="bg-gray-200 px-1 rounded text-gray-700">Authorization</code> header.
        </p>

        {/* List templates */}
        <div className="mb-4">
          <div className="text-xs font-medium text-gray-600 mb-1">List templates</div>
          <div className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs font-mono overflow-x-auto whitespace-pre">{`curl -H "Authorization: Bearer csk_YOUR_KEY" \\
  ${window.location.origin}/api/v1/templates`}</div>
        </div>

        {/* Get template detail */}
        <div className="mb-4">
          <div className="text-xs font-medium text-gray-600 mb-1">Get template details &amp; inputs</div>
          <div className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs font-mono overflow-x-auto whitespace-pre">{`curl -H "Authorization: Bearer csk_YOUR_KEY" \\
  ${window.location.origin}/api/v1/templates/:id`}</div>
        </div>

        {/* Generate — text only */}
        <div className="mb-4">
          <div className="text-xs font-medium text-gray-600 mb-1">Generate (text prompt only)</div>
          <div className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs font-mono overflow-x-auto whitespace-pre">{`curl -X POST -H "Authorization: Bearer csk_YOUR_KEY" \\
  -F "additional_text=A sunset over mountains" \\
  ${window.location.origin}/api/v1/templates/:id/generate`}</div>
        </div>

        {/* Generate — with images */}
        <div className="mb-4">
          <div className="text-xs font-medium text-gray-600 mb-1">Generate (with reference images)</div>
          <p className="text-xs text-gray-500 mb-1.5">
            Images are sent as <code className="bg-gray-200 px-1 rounded text-gray-700">multipart/form-data</code> file uploads using the <code className="bg-gray-200 px-1 rounded text-gray-700">images</code> field. You can send up to 10 images. Each template already has its own source images — these are <em>additional</em> reference images.
          </p>
          <div className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs font-mono overflow-x-auto whitespace-pre">{`curl -X POST -H "Authorization: Bearer csk_YOUR_KEY" \\
  -F "additional_text=Match the style of these photos" \\
  -F "images=@photo1.jpg" \\
  -F "images=@photo2.png" \\
  ${window.location.origin}/api/v1/templates/:id/generate`}</div>
        </div>

        {/* Response format note */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="text-xs font-medium text-gray-600 mb-1">Response format</div>
          <div className="bg-gray-900 text-gray-100 rounded-md p-3 text-xs font-mono overflow-x-auto whitespace-pre">{`{
  "success": true,
  "data": {
    "media_type": "image",       // "image" | "video" | "text"
    "image_url": "https://...",  // for images
    "video_url": "https://...",  // for videos
    "text_content": "...",       // for text
    "model": "gemini-..."
  }
}`}</div>
        </div>

        {/* Timeout / latency guidance */}
        <div className="mt-4 pt-3 border-t border-gray-200">
          <div className="text-xs font-medium text-gray-600 mb-1">⏱ Response times &amp; timeouts</div>
          <p className="text-xs text-gray-500 leading-relaxed">
            Generation requests are <strong>synchronous</strong> — the response is returned once the media is fully generated. Expect the following response times:
          </p>
          <ul className="mt-1.5 text-xs text-gray-500 list-disc list-inside space-y-0.5">
            <li><strong>Image models:</strong> 15 – 60 seconds depending on the model and resolution</li>
            <li><strong>Video models:</strong> 1 – 5 minutes depending on duration and complexity</li>
            <li><strong>Text models:</strong> 5 – 30 seconds</li>
          </ul>
          <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">
            Set your HTTP client timeout to at least <strong>5 minutes</strong> to avoid premature disconnects, especially when using video models. The API enforces its own rate limits (120 reads/min, 10 generations/min per key).
          </p>
        </div>
      </div>

      {/* Key list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner size="md" />
        </div>
      ) : keys.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
            <KeyRound className="w-7 h-7 text-gray-400" />
          </div>
          <p className="text-gray-600 font-medium mb-1">No API keys</p>
          <p className="text-sm text-gray-400 mb-4">
            Create an API key to start using the programmatic API.
          </p>
          <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
            Create Your First Key
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active keys */}
          {activeKeys.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Active Keys ({activeKeys.length})
              </h3>
              <div className="space-y-2">
                {activeKeys.map((key) => (
                  <ApiKeyRow
                    key={key.id}
                    apiKey={key}
                    onRevoke={setRevokeTarget}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Revoked keys */}
          {revokedKeys.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-gray-400 mb-2">
                Revoked Keys ({revokedKeys.length})
              </h3>
              <div className="space-y-2">
                {revokedKeys.map((key) => (
                  <ApiKeyRow
                    key={key.id}
                    apiKey={key}
                    onRevoke={setRevokeTarget}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <CreateKeyModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />

      {revokeTarget && (
        <RevokeModal
          keyName={revokeTarget.name}
          isOpen={!!revokeTarget}
          onClose={() => setRevokeTarget(null)}
          onConfirm={handleRevoke}
          isRevoking={isRevoking}
        />
      )}
    </PageContainer>
  );
}
